import {
	MAX_SPECIALTY_PILLS,
	SESSION_FORMAT_LABELS,
	type SessionFormat,
	SPECIALTIES,
} from "@klarify/constants";
import { labelForId } from "@klarify/utilities";
import { SESSION_FORMAT_ICONS } from "./shared";

/**
 * Shared footer for the therapist and clinic directory cards: specialty pills
 * (capped at MAX_SPECIALTY_PILLS with a "+N more" overflow) plus session-format
 * badges. Renders nothing when there's neither. `moreLabel` is passed in so each
 * card keeps its own i18n namespace for the overflow label.
 */
export const DirectoryCardFooter = ({
	specialties,
	sessionFormats,
	moreLabel,
}: {
	specialties: string[];
	sessionFormats: SessionFormat[];
	moreLabel: string;
}) => {
	const visibleSpecialties = specialties.slice(0, MAX_SPECIALTY_PILLS);
	const remainingSpecialties = Math.max(
		0,
		specialties.length - visibleSpecialties.length,
	);
	if (sessionFormats.length === 0 && visibleSpecialties.length === 0) {
		return null;
	}

	return (
		<div className="mt-auto flex flex-col gap-2.5 border-klarify-neutral-100 border-t bg-klarify-cloud-50/40 px-5 py-3.5 sm:px-6">
			{visibleSpecialties.length > 0 ? (
				<div className="flex flex-wrap items-center gap-2">
					{visibleSpecialties.map((id) => (
						<span
							key={id}
							className="rounded-md border border-klarify-neutral-200 bg-klarify-neutral-50 px-2.5 py-1 text-[13px] text-klarify-neutral-700 leading-snug"
						>
							{labelForId(SPECIALTIES, id)}
						</span>
					))}
					{remainingSpecialties > 0 ? (
						<span className="px-1 text-[12px] text-klarify-neutral-500 leading-snug">
							+{remainingSpecialties} {moreLabel}
						</span>
					) : null}
				</div>
			) : null}
			{sessionFormats.length > 0 ? (
				<div className="flex flex-wrap items-center gap-2">
					{sessionFormats.map((f) => {
						const Icon = SESSION_FORMAT_ICONS[f];
						return (
							<span
								key={f}
								className="inline-flex items-center gap-2 rounded-md border border-klarify-neutral-200 bg-klarify-neutral-50 px-3 py-1.5 font-medium text-klarify-neutral-800 text-sm"
							>
								<Icon className="h-4 w-4 text-klarify-forest-700" />
								{SESSION_FORMAT_LABELS[f]}
							</span>
						);
					})}
				</div>
			) : null}
		</div>
	);
};

DirectoryCardFooter.displayName = "DirectoryCardFooter";
